import { UserHelper } from '@/helpers/user.helper';
import { Utils } from '@/helpers/utils';
import { AdressDataModel } from '@/models/AdressResulModel';
import { BaseField, colSize, inputType } from '@/models/BaseField';
import { ApiAdresseRepo } from '@/repositories/api-adresse/api-addresse';
import { AppConfigService } from '@/services/app-config.service';
import { NgFor, NgIf } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, FormControlDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  CompaniesService,
  Company,
  CompanyPaginatedList,
  PropertiesService,
  PropertyCompanyService,
  RealProperty,
  RealPropertyPaginatedList,
  ServiceType,
  User,
  UserPaginatedList,
  UsersService
} from '@eemo/api-client';
import { UserSummaryDetail } from '@eemo/api-client/model/userSummaryDetail';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EmptyToNullDirective } from '../../../directives/forms/empty-to-null/empty-to-null.directive';
import { TooltipDirective } from '../../../directives/tooltip/tooltip.directive';
import { ErrorMessageComponent } from '../error-message/error-message.component';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    TooltipDirective,
    NgFor,
    MatOptionModule,
    ErrorMessageComponent,
    EmptyToNullDirective,
    TranslateModule
  ]
})
export class InputComponent
  extends BaseField<string, string>
  implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor
{
  readonly INPUT_ADDRESS_MIN_LENGTH_STREET = 5;
  readonly INPUT_USERS_MIN_LENGTH = 2;
  readonly INPUT_PROPERTIES_MIN_LENGTH = 2;
  readonly INPUT_COMPANIES_MIN_LENGTH = 2;

  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;
  @Input() label: string | null = 'label manquant'; // translate key
  @Input() type: inputType;
  @Input() isTextarea = false;
  @Input() size: colSize = 6;
  @Input() autocompletionType: null | 'address' | 'users' | 'contacts' | 'properties' | 'company' | 'propertyService' =
    null;
  @Input() autocompletionQP: HttpParams | null = null;
  @Input() autocompletionSearchEvenIfEmpty: HttpParams | null = null;
  @Input() autocompletionExcludeIdList: string[] = [];
  @Input() autocompletionHideNoResult = false;
  @Input() displayType: 'default' | 'email' | 'email-or-user' = 'default';
  @Input() hasDeleteBtn = false;
  @Input() hasValidateBtn = false;
  @Input() isDisabled = false;
  @Input() disableUsersWithCompany = false;
  @Input() suffixIcon: string | null;
  @Input() suffixText: string | null;
  @Input() prefixIcon: string | null;
  @Input() hint: string | null;
  @Input() patternErrorLabel: string = null;
  @Input() useLabelTranslation = true;
  @Output() clickedOption = new EventEmitter<any>();
  @Output() clickedDelete = new EventEmitter<void>();
  @Output() clickedValidate = new EventEmitter<void>();
  @Output() valueChanged = new EventEmitter<any>();

  autoCompletedOptions: any[] = [];
  autocompletionSearchApiSub = new Subscription();
  autocompletionDisplayNames: string[] = [];
  autocompletionDisabled: boolean[] = [];
  autocompletionShowNoResult = false;

  isBrowserSupportsDateInput = Utils.isBrowserSupportsDateInput();

  constructor(
    @Self() @Optional() protected override control: FormControlDirective,
    private apiAdresseRepo: ApiAdresseRepo,
    private usersService: UsersService,
    private userHelper: UserHelper,
    private propertiesService: PropertiesService,
    private companiesService: CompaniesService,
    private translate: TranslateService,
    private el: ElementRef,
    private appConfigService: AppConfigService
  ) {
    super(control);
    this.control && (this.control.valueAccessor = this);
  }

  ngOnInit(): void {
    if (this.size > 0) {
      (this.el.nativeElement as HTMLElement).classList.add('col-md-' + this.size);
    }
  }

  ngOnDestroy(): void {
    this.autocompletionSearchApiSub.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.manageDateValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isDisabled']) {
      this.setDisabled(this.isDisabled);
    }
  }

  manageDateValue() {
    if (
      (this.type === 'date' || this.type === 'datetime-local') &&
      this.inputElement &&
      this.appConfigService.isBrowserSafari
    ) {
      // hack for safari which initializes null date to current date
      // add a css class when value is null
      if (this.control.control.value === null) {
        this.inputElement.nativeElement.classList.add('null-date');
      } else {
        this.inputElement.nativeElement.classList.remove('null-date');
      }
    }
  }

  setValue(value: any): void {
    this.manageDateValue();
  }

  setDisabled(isDisabled: boolean): void {
    if (isDisabled) {
      this.control.control.disable();
    } else {
      this.control.control.enable();
    }
  }

  onChange(e: any) {}

  onChangeModel(e: any) {
    if (typeof this.control.control.value === 'string' && this.control.control.value?.trim() === '') {
      this.control.control.setValue(null);
    }
    if (this.autocompletionType && !this.control.control.value) {
      this.autoCompletedOptions = [];
      this.autocompletionDisplayNames = [];
    }

    this.manageDateValue();

    this.valueChanged.emit(this.control.control.value);
  }

  onClickOption(o: any, isDiabled: boolean) {
    if (!isDiabled) {
      this.clickedOption.next(o);
    }
  }

  onClickDeleteBtn() {
    this.clickedDelete.next();
  }

  onClickValidateBtn() {
    this.clickedValidate.next();
  }

  displayFn(object: any): string {
    if (this.displayType === 'email') return object;
    if (this.displayType === 'email-or-user') {
      if (object?.uid) {
        const user: User = object;
        return user ? UserHelper.userDisplayName(user) : '';
      } else return object;
    }
    if (this.autocompletionType === 'address') {
      return object as string;
    } else if (this.autocompletionType === 'users') {
      const user: User = object;
      //if (this.displayType === 'email') return user ? user.email : '';
      return user ? UserHelper.userDisplayName(user) : '';
    } else if (this.autocompletionType === 'contacts') {
      const user: UserSummaryDetail = object;
      //if (this.displayType === 'email') return user ? user.email : '';
      return user ? UserHelper.userSummmaryDisplayName(user) : '';
    } else if (this.autocompletionType === 'properties') {
      const property: RealProperty = object;
      return property?.name || '';
    } else if (this.autocompletionType === 'company') {
      const company: Company = object;
      return company?.name || '';
    } else if (this.autocompletionType === 'propertyService') {
      const propertyCompanyService: PropertyCompanyService = object;
      return propertyCompanyService?.name || '';
    } else {
      return this.control.control.value;
    }
  }

  // ----------------------------------------------------------------------------------- //
  //                              APi for autocompletion
  // ----------------------------------------------------------------------------------- //
  onSearch(search: InputEvent) {
    if (this.autocompletionType) {
      const val = (search.target as HTMLInputElement).value;

      if (this.autocompletionType === 'address') {
        if (val && val.length >= this.INPUT_ADDRESS_MIN_LENGTH_STREET) {
          this.searchAddress(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      } else if (this.autocompletionType === 'users') {
        if (val && val.length >= this.INPUT_USERS_MIN_LENGTH) {
          this.searchUsers(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      } else if (this.autocompletionType === 'contacts') {
        if (val && val.length >= this.INPUT_USERS_MIN_LENGTH) {
          this.searchContacts(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      } else if (this.autocompletionType === 'properties') {
        if (val && val.length >= this.INPUT_PROPERTIES_MIN_LENGTH) {
          this.searchProperties(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      } else if (this.autocompletionType === 'company') {
        if ((this.autocompletionSearchEvenIfEmpty && !val) || (val && val.length >= this.INPUT_COMPANIES_MIN_LENGTH)) {
          this.searchCompanies(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      } else if (this.autocompletionType === 'propertyService') {
        if ((this.autocompletionSearchEvenIfEmpty && !val) || (val && val.length >= this.INPUT_COMPANIES_MIN_LENGTH)) {
          this.searchCompanies(val);
        } else {
          this.autoCompletedOptions = [];
          this.autocompletionDisplayNames = [];
        }
      }
    }
  }

  onFocusSearch(search: InputEvent) {
    if (this.autocompletionSearchEvenIfEmpty) {
      this.onSearch(search);
    }
  }

  searchAddress(search: string) {
    this.autocompletionSearchApiSub = this.apiAdresseRepo.search(search).subscribe((res: AdressDataModel[]) => {
      this.autoCompletedOptions = res || [];
      this.autocompletionDisplayNames = this.autoCompletedOptions.map((a: AdressDataModel) => a.fullAddress);
      this.autocompletionShowNoResult = this.autoCompletedOptions.length === 0;
    });
  }

  searchUsers(name: string) {
    let roles = null;
    if (this.autocompletionQP && this.autocompletionQP['users']) {
      roles = this.autocompletionQP['users'];
    }
    this.autocompletionSearchApiSub = this.usersService.getUsers(roles, name).subscribe((res: UserPaginatedList) => {
      this.autoCompletedOptions = res.list?.filter((r) => !this.autocompletionExcludeIdList.includes(r.uid)) || [];
      this.autocompletionDisplayNames = this.autoCompletedOptions.map((a: User) => UserHelper.userDisplayName(a, true));
      this.autocompletionShowNoResult = this.autoCompletedOptions.length === 0;
      if (this.disableUsersWithCompany)
        this.autocompletionDisabled = this.autoCompletedOptions.map((a: User) =>
          a.companies?.length > 0 ? true : false
        );
    });
  }

  searchContacts(name: string) {
    let roles = null;
    if (this.autocompletionQP && this.autocompletionQP['users']) {
      roles = this.autocompletionQP['users'];
    }
    this.autocompletionSearchApiSub = this.usersService
      .searchUserContacts(this.userHelper.currentUser.userId, roles, name)
      .subscribe((res: UserSummaryDetail[]) => {
        this.autoCompletedOptions = res.filter((r) => !this.autocompletionExcludeIdList.includes(r.uid)) || [];
        this.autocompletionDisplayNames = this.autoCompletedOptions.map((a: UserSummaryDetail) =>
          UserHelper.userSummmaryDisplayName(a)
        );
        this.autocompletionShowNoResult = this.autoCompletedOptions.length === 0;
      });
  }

  searchProperties(name: string) {
    this.autocompletionSearchApiSub = this.propertiesService
      .getRealPropertyList(null, null, null, name)
      .subscribe((res: RealPropertyPaginatedList) => {
        this.autoCompletedOptions = res.list?.filter((r) => !this.autocompletionExcludeIdList.includes(r.uid)) || [];
        this.autocompletionDisplayNames = this.autoCompletedOptions.map(
          (a: RealProperty) => a?.name + (a?.owner ? ' (' + UserHelper.userSummmaryDisplayName(a.owner) + ')' : '')
        );
        this.autocompletionShowNoResult = this.autoCompletedOptions.length === 0;
      });
  }

  searchCompanies(name: string) {
    let services: ServiceType[] | null = null;
    if (this.autocompletionQP && this.autocompletionQP['services']) {
      services = this.autocompletionQP['services'];
    }
    if (!name) {
      name = null;
    }
    this.autocompletionSearchApiSub = this.companiesService
      .getCompanies(services, name)
      .subscribe((res: CompanyPaginatedList) => {
        this.autoCompletedOptions = res.list?.filter((r) => !this.autocompletionExcludeIdList.includes(r.uid)) || [];
        this.autocompletionDisplayNames = this.autoCompletedOptions.map(
          (a: Company) => a?.name + this.getServiceTr(a?.services)
        );
        this.autocompletionShowNoResult = this.autoCompletedOptions.length === 0;
      });
  }

  getServiceTr(s: ServiceType[]) {
    if (!s) {
      return '';
    }
    let ret = ' (';
    ret += s.map((se) => this.translate.instant('enum.ServiceType.' + se));
    ret += ')';
    return ret;
  }
}
